import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { resetStores } from '@datorama/akita';
import { jwtDecode } from 'jwt-decode';
import { Observable, of, ReplaySubject, throwError } from 'rxjs';
import { catchError, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { JwtToken } from './jwt-token';
import { LoginResponse } from './login-response';
import { UserWithRolesDto } from './user-with-roles-dto';
import { SparrowAppEnum } from '../enums/sparrow-app.enum';
import { ApiResponse } from '../models/api-response';
import { CatalogueDto } from '../models/catalogue-with-format-dto';
import { OperatorPermissionsDto } from '../models/operator-level-permissions-dto';
import { UserPreferencesWrite } from '../models/user-dto';
import { setCookie } from '../utils';

@Injectable({ providedIn: 'root' })
export class AuthService {
  userWithRoles: UserWithRolesDto | undefined;
  operatorPermissions$: Observable<OperatorPermissionsDto | null>;
  operatorCatalogues$: Observable<CatalogueDto[] | null>;

  private userSubject: ReplaySubject<UserWithRolesDto> = new ReplaySubject<UserWithRolesDto>(1);

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {
    this.operatorPermissions$ = this.userSubject.pipe(
      switchMap(() => this.getOperatorLevelPermissions()),
      shareReplay(1),
    );
    this.operatorCatalogues$ = this.userSubject.pipe(
      switchMap(() => this.getUserCatalogues()),
      shareReplay(1),
    );

    const userData = localStorage.getItem('userWithRoles');
    if (userData) {
      this.userWithRoles = JSON.parse(userData);
      this.userSubject.next(this.userWithRoles);
    }
  }

  getUserLoginData(): Observable<UserWithRolesDto | null> {
    return this.http.get<UserWithRolesDto>(`${environment.backendUrl}/api/v1/user-login-data/me`).pipe(
      tap((rsp) => {
        this.userTimezoneSetup(rsp);
        this.updateUserWithRoles(rsp);
      }),
      catchError((err) => (err.status === 404 ? of(null) : throwError(err))),
    );
  }

  updateUserWithRoles(data: UserWithRolesDto) {
    this.userWithRoles = data;
    this.userSubject.next(data);
    localStorage.setItem('userWithRoles', JSON.stringify(data));
  }

  storeUserPreferences(userId: number, data: UserPreferencesWrite): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${environment.backendUrl}/api/v1/users/${userId}/preferences`, data);
  }

  loginAndSetData(loginData: { username: string; password: string }): Observable<UserWithRolesDto> {
    return this.http.post<LoginResponse>(`${environment.backendUrl}/api/v1/jwt/login`, loginData).pipe(
      switchMap((res: LoginResponse) => {
        this.storeTokenData(res);
        return this.getUserLoginData();
      }),
    );
  }

  getMahleAuthRequest() {
    return this.http.get(`${environment.backendUrl}/api/v1/mahle/authRequest`);
  }

  calculateOperatorLandingPage(userWithRoles: UserWithRolesDto): string[] {
    const hasSMPermission = userWithRoles.userRolesWithOrgUnits.some(
      (value) => value.role.sparrowApplication === SparrowAppEnum.StockManagement,
    );

    const hasCAPermission = userWithRoles.userRolesWithOrgUnits.some(
      (value) => value.role.sparrowApplication === SparrowAppEnum.CustomerApproval,
    );

    const hasPlanPermission = userWithRoles.userRolesWithOrgUnits.some(
      (value) => value.role.sparrowApplication === SparrowAppEnum.Planning,
    );

    const hasPoolPermission = userWithRoles.userRolesWithOrgUnits.some(
      (value) => value.role.sparrowApplication === SparrowAppEnum.Pool,
    );

    // ORDER: SM, Pool, Plan, Clean
    if (hasSMPermission) {
      return ['/stock-management'];
    }
    if (hasPoolPermission) {
      return ['/pool'];
    }
    if (hasPlanPermission) {
      return ['/planning-tool'];
    }
    if (hasCAPermission) {
      return ['/data-cleaning'];
    }
    return ['/'];
  }

  mahleLogin(samlMessage: String): Observable<UserWithRolesDto> {
    return this.http.post<LoginResponse>(`${environment.backendUrl}/api/v1/mahle/login/${samlMessage}`, {}).pipe(
      switchMap((res: LoginResponse) => {
        this.storeTokenData(res);
        return this.getUserLoginData();
      }),
    );
  }

  getAvebeAuthRequest() {
    return this.http.get(`${environment.backendUrl}/api/v1/avebe/authRequest`);
  }

  avebeLogin(authRequestId: String): Observable<UserWithRolesDto> {
    return this.http.post<LoginResponse>(`${environment.backendUrl}/api/v1/avebe/login/${authRequestId}`, {}).pipe(
      switchMap((res: LoginResponse) => {
        this.storeTokenData(res);
        return this.getUserLoginData();
      }),
    );
  }

  getBayerAuthRequest() {
    return this.http.get(`${environment.backendUrl}/api/v1/bayer/authRequest`);
  }

  bayerLogin(authRequestId: String): Observable<UserWithRolesDto> {
    return this.http.post<LoginResponse>(`${environment.backendUrl}/api/v1/bayer/login/${authRequestId}`, {}).pipe(
      switchMap((res: LoginResponse) => {
        this.storeTokenData(res);
        return this.getUserLoginData();
      }),
    );
  }

  refreshToken(): Observable<{ accessToken: string; refreshToken: string }> {
    const data = {
      refreshToken: localStorage.getItem('refreshToken'),
    };
    return this.http
      .post<{ accessToken: string; refreshToken: string }>(`${environment.backendUrl}/api/v1/jwt/refreshToken`, data)
      .pipe(
        tap((response) => {
          localStorage.setItem('accessToken', response.accessToken);
          localStorage.setItem('refreshToken', response.refreshToken);
        }),
      );
  }

  getOperatorLevelPermissions(): Observable<OperatorPermissionsDto | null> {
    return this.http
      .get<OperatorPermissionsDto>(`${environment.backendUrl}/api/v1/operator-permissions/user/me`)
      .pipe(catchError((err) => (err.status === 404 ? of(null) : throwError(err))));
  }

  getUserCatalogues(): Observable<CatalogueDto[]> {
    return this.http
      .get<CatalogueDto[]>(`${environment.backendUrl}/api/v1/catalogue/operator-catalogues`)
      .pipe(catchError((err) => (err.status === 404 ? of(null) : throwError(err))));
  }

  isLoggedIn(): boolean {
    const userExist = localStorage.getItem('userWithRoles');
    const accessToken = this.getAccessToken();
    const isTokenExpired = accessToken ? Date.now() >= this.decodeToken(accessToken).exp * 1000 : false;
    return !!userExist && !!accessToken && !isTokenExpired;
  }

  logout() {
    this.router.navigate(['/login']);
    this.clearData();
  }

  clearData() {
    sessionStorage.clear();
    localStorage.removeItem('userWithRoles');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('pageLoadTime');
    this.userWithRoles = null;
    this.userSubject.next(this.userWithRoles);
    setTimeout(() => resetStores(), 500);
  }

  getAccessToken(): string {
    return localStorage.getItem('accessToken');
  }

  resetPassword(email: string): Observable<any> {
    return this.http.post(`${environment.backendUrl}/api/v1/password-reset`, { email });
  }

  setPassword(password: string, token: string): Observable<any> {
    return this.http.post(
      `${environment.backendUrl}/api/v1/users/password-set`,
      { password },
      {
        headers: new HttpHeaders().set('Authorization', `Bearer ${token}`),
      },
    );
  }

  decodeToken(token: string): JwtToken {
    return jwtDecode(token);
  }

  private getUserTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  private userTimezoneSetup(data: UserWithRolesDto) {
    const timeZone = this.getUserTimezone();
    setCookie('SPARROW_TIMEZONE', timeZone, 99999999);
    if (!!timeZone && data.user.timeZone !== timeZone) {
      this.storeUserPreferences(data.user.id, { timeZone }).subscribe();
    }
  }

  private storeTokenData(res: LoginResponse) {
    const user = this.decodeToken(res.accessToken).data;
    const tempLoginLsUser = JSON.parse(localStorage.getItem('userWithRoles'));
    if (tempLoginLsUser && tempLoginLsUser.id === user.userId) {
      localStorage.clear();
    }
    localStorage.setItem('accessToken', res.accessToken);
    localStorage.setItem('refreshToken', res.refreshToken);
  }
}
